import { useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import Checkbox from 'components/shared/form/Checkbox';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SegmentedControl from 'components/shared/SegmentedControl';
import { useProfile } from 'context/profile';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexGroups } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexLocations } from 'services/api/admin/locations';
import {
  ADMIN_MAPPING_TARGETS_QUERY_KEY,
  adminMappingImpact,
  adminMappingsQueryKeys,
  DONT_MAP,
  MappingType,
} from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';
import { BodySmall, LabelBold } from 'styles/typography';

import ImpactLine from './ImpactLine';
import { TYPE_NOUN } from './operations';

type Option = { value: string; label: string; inNetwork?: boolean };

export type MapPanelResult = { key: string; target: string; label: string; addToNetwork: boolean };

type Props = {
  configId: string;
  clientId: string;
  ehr: boolean;
  type: MappingType;
  mappingKey: string | null;
  networkAddable?: boolean;
  currentLabel: string | null;
  initialMode?: 'map' | 'dont_map';
  onCancel: () => void;
  onSave: (result: MapPanelResult) => void;
};

function useTargetOptions(clientId: string, type: MappingType, ehr: boolean, outsideNetwork: boolean) {
  const groupParams = outsideNetwork ? {} : { mappingNetworkOf: clientId };

  const groups = useInfiniteOptions({
    queryKey: [...ADMIN_MAPPING_TARGETS_QUERY_KEY, 'groups', clientId, groupParams],
    fetchFn: (params: any, signal?: any) =>
      adminIndexGroups<'groupType'>(params, signal, ['groupType']).then((page) => ({
        ...page,
        data: page.data.map((g) => ({
          value: g.id,
          label: [g.name, g.address?.city, g.groupType?.displayName].filter(Boolean).join(' · '),
          inNetwork: g.inNetwork,
        })),
      })),
    params: { ...groupParams, inNetworkOf: clientId, sortBy: 'name asc' },
  });
  const classifications = useInfiniteOptions({
    queryKey: [...ADMIN_MAPPING_TARGETS_QUERY_KEY, type, clientId, ehr],
    fetchFn: (params: any, signal?: any) =>
      adminIndexClassifications(params, signal).then((page) => ({
        ...page,
        data: page.data.map((c) => ({ value: c.name, label: c.name })),
      })),
    params: {
      type: type === 'plan_types' ? 'plan_type' : 'episode',
      ...(!ehr || type === 'plan_types' ? { client: clientId } : {}),
      sortBy: 'name asc',
    },
  });
  const groupTypes = useInfiniteOptions({
    queryKey: [...ADMIN_MAPPING_TARGETS_QUERY_KEY, 'group_types', ehr],
    fetchFn: (params: any, signal?: any) =>
      indexGroupTypes(params, signal).then((page) => ({
        ...page,
        data: page.data.map((t) => ({ value: t.name, label: t.displayName || t.name })),
      })),
    params: { ...(ehr ? { type: 'provider' } : {}), sortBy: 'displayName asc' },
  });
  const locations = useInfiniteOptions({
    queryKey: [...ADMIN_MAPPING_TARGETS_QUERY_KEY, 'locations', clientId],
    fetchFn: (params: any, signal?: any) =>
      adminIndexLocations(params, signal).then((page) => ({
        ...page,
        data: page.data.map((l) => ({ value: l.id, label: l.name })),
      })),
    params: { clientId, sortBy: 'name asc' },
  });

  return { groups, plan_types: classifications, episodes: classifications, group_types: groupTypes, locations }[type];
}

export default function MapPanel({
  configId,
  clientId,
  ehr,
  type,
  mappingKey,
  networkAddable = true,
  currentLabel,
  initialMode = 'map',
  onCancel,
  onSave,
}: Props) {
  const canDontMap = type !== 'group_types';
  const groupPicker = type === 'groups';
  const [key, setKey] = useState(mappingKey ?? '');
  const [mode, setMode] = useState<'map' | 'dont_map'>(initialMode);
  const [outsideNetwork, setOutsideNetwork] = useState(false);
  const [selected, setSelected] = useState<Option | null>(null);
  const [confirmingNetwork, setConfirmingNetwork] = useState(false);
  const { profile } = useProfile();
  const canAddToNetwork = profile.has(PERMISSIONS.adminClientEdit);
  const targetOptions = useTargetOptions(clientId, type, ehr, outsideNetwork);

  const target = mode === 'dont_map' ? DONT_MAP : selected?.value;
  const trimmedKey = key.trim();
  const outOfNetwork = groupPicker && mode === 'map' && !!selected && selected.inNetwork === false;

  const impact = useQuery({
    queryKey: adminMappingsQueryKeys.impact(configId, type, trimmedKey, target),
    queryFn: () => adminMappingImpact(configId, type, trimmedKey, target),
    enabled: ehr && !!target && !!trimmedKey && !!mappingKey,
    retry: false,
  });

  const finish = (addToNetwork: boolean) => {
    if (!target || !trimmedKey) return;
    onSave({
      key: trimmedKey,
      target,
      label: mode === 'dont_map' ? "Don't map" : (selected?.label ?? target),
      addToNetwork,
    });
  };

  const use = () => (outOfNetwork ? setConfirmingNetwork(true) : finish(false));

  if (confirmingNetwork && selected) {
    return (
      <Panel>
        <PanelTitle>Add to network?</PanelTitle>
        <BodySmall>
          {selected.label} isn&apos;t in this client&apos;s network.{' '}
          {canAddToNetwork
            ? "It's added when you submit, and stays in the network if the mapping changes later."
            : "Adding a group to a client's network needs client edit permission, so someone with it has to add this one."}
        </BodySmall>
        <ButtonGroup>
          <Button variant='ghost' onClick={() => setConfirmingNetwork(false)}>
            Back
          </Button>
          <Button onClick={() => finish(true)} disabled={!canAddToNetwork}>
            Add to network and map
          </Button>
        </ButtonGroup>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelTitle>{mappingKey ? 'Change mapping' : 'Add mapping'}</PanelTitle>
      {mappingKey ? (
        <ReceivedValue title={mappingKey}>{mappingKey}</ReceivedValue>
      ) : (
        <InputGroup title='Received value'>
          <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder='Value exactly as received' />
        </InputGroup>
      )}
      {currentLabel && (
        <BodySmall>
          Currently maps to <LabelBold>{currentLabel}</LabelBold>
        </BodySmall>
      )}

      {canDontMap && (
        <SegmentedControl
          fullWidth
          options={[
            { label: `Map to a ${TYPE_NOUN[type]}`, value: 'map' },
            { label: "Don't map", value: 'dont_map' },
          ]}
          selectedValue={mode}
          onChange={(value) => setMode(value as 'map' | 'dont_map')}
        />
      )}

      {mode === 'map' ? (
        <>
          <InputGroup title={`Olio ${TYPE_NOUN[type]}`}>
            <Select<Option>
              {...targetOptions}
              value={selected}
              onChange={setSelected}
              getOptionValue={(o) => o.value}
              getOptionLabel={(o) => o.label}
              placeholder={
                groupPicker && !outsideNetwork ? "Search this client's network" : `Search ${TYPE_NOUN[type]}s`
              }
              menuPortalTarget={document.body}
            />
          </InputGroup>
          {groupPicker && networkAddable && (
            <Checkbox
              checked={outsideNetwork}
              onChange={(checked) => {
                setOutsideNetwork(checked);
                setSelected(null);
              }}
              label="Include groups outside this client's network"
            />
          )}
        </>
      ) : (
        <Hint>Olio treats this value as known and leaves the field blank. The agent never maps it.</Hint>
      )}

      <ImpactLine impact={impact.data} />
      <Hint>Applies when you submit the import configuration.</Hint>

      <ButtonGroup>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={use} disabled={!target || !trimmedKey}>
          {mode === 'dont_map' ? "Mark don't map" : 'Use this mapping'}
        </Button>
      </ButtonGroup>
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const ReceivedValue = styled.div`
  font-family: monospace;
  font-size: 14px;
  padding: 8px 12px;
  background: ${colors.black5};
  border-radius: 4px;
  overflow-wrap: anywhere;
`;

const Hint = styled(BodySmall)`
  color: ${colors.black75};
  margin: 0;
`;
